import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Clock,
  Phone,
  Mail,
  RefreshCcw,
  AlertTriangle,
  CheckCircle2,
  Timer,
  Loader2,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { toast } from "sonner";

type FilterStatus = "all" | "to_contact" | "contacted_1" | "expired_unresolved";

function getTimeRemaining(refundEligibleAt: string | null): { hours: number; minutes: number; totalMs: number } {
  if (!refundEligibleAt) return { hours: 0, minutes: 0, totalMs: 0 };
  const ms = new Date(refundEligibleAt).getTime() - Date.now();
  const totalHours = Math.max(0, ms) / (1000 * 60 * 60);
  return {
    hours: Math.floor(totalHours),
    minutes: Math.floor((totalHours % 1) * 60),
    totalMs: ms,
  };
}

function getUrgencyColor(refundEligibleAt: string | null): string {
  if (!refundEligibleAt) return "text-muted-foreground";
  const { totalMs } = getTimeRemaining(refundEligibleAt);
  const hours = totalMs / (1000 * 60 * 60);
  if (totalMs <= 0) return "text-muted-foreground";
  if (hours < 12) return "text-red-600 font-semibold";
  if (hours < 24) return "text-orange-500 font-medium";
  return "text-green-600";
}

function CountdownCell({ refundEligibleAt }: { refundEligibleAt: string | null }) {
  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(interval);
  }, []);

  if (!refundEligibleAt) return <span className="text-muted-foreground">-</span>;

  const { hours, minutes, totalMs } = getTimeRemaining(refundEligibleAt);
  const colorClass = getUrgencyColor(refundEligibleAt);

  if (totalMs <= 0) {
    return <span className="text-muted-foreground">Expiré</span>;
  }

  return (
    <span className={colorClass}>
      {hours}h {minutes.toString().padStart(2, "0")}m
    </span>
  );
}

function deriveStatus(item: {
  contactAttempt1At: string | null;
  contactAttempt2At: string | null;
  refundEligibleAt: string | null;
}): "to_contact" | "contacted_1" | "contacted_2" | "expired" {
  const expired = item.refundEligibleAt
    ? new Date(item.refundEligibleAt).getTime() <= Date.now()
    : false;

  if (item.contactAttempt2At) return expired ? "expired" : "contacted_2";
  if (item.contactAttempt1At) return expired ? "expired" : "contacted_1";
  return expired ? "expired" : "to_contact";
}

const STATUS_CONFIG = {
  to_contact: { label: "À contacter", variant: "default" as const },
  contacted_1: { label: "Contacté 1x", variant: "secondary" as const },
  contacted_2: { label: "Contacté 2x", variant: "outline" as const },
  expired: { label: "Expiré", variant: "destructive" as const },
};

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("fr-FR", DATE_FORMAT);
}

export default function SLALeads() {
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);
  const [filter, setFilter] = useState<FilterStatus>("all");
  const [page, setPage] = useState(1);
  const [refundTarget, setRefundTarget] = useState<{ id: string; projectTitle: string; proCompanyName: string } | null>(null);
  const [refundReason, setRefundReason] = useState("");
  const utils = trpc.useUtils();

  const { data, isLoading, isError } = trpc.admin.leadSla.getLeadSLAStatus.useQuery(
    { page, limit: 50 },
    { refetchInterval: 60_000 }
  );

  const contactMutation = trpc.admin.leadSla.markContactAttempt.useMutation({
    onSuccess: (_data, variables) => {
      toast.success(`Contact ${variables.attempt} enregistré`);
      utils.admin.leadSla.getLeadSLAStatus.invalidate();
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const refundMutation = trpc.admin.leadSla.refundLead.useMutation({
    onSuccess: (result) => {
      toast.success(`${result.creditsRefunded} crédit(s) remboursé(s)`);
      utils.admin.leadSla.getLeadSLAStatus.invalidate();
      setRefundTarget(null);
      setRefundReason("");
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const items = data?.items ?? [];

  const filtered = items.filter((item) => {
    const status = deriveStatus(item);
    if (filter === "all") return true;
    if (filter === "to_contact") return status === "to_contact";
    if (filter === "contacted_1") return status === "contacted_1";
    if (filter === "expired_unresolved") return status === "expired";
    return true;
  });

  const urgentCount = items.filter((item) => {
    const { totalMs } = getTimeRemaining(item.refundEligibleAt);
    return totalMs > 0 && totalMs < 12 * 60 * 60 * 1000;
  }).length;

  const expiredCount = items.filter((item) => {
    const { totalMs } = getTimeRemaining(item.refundEligibleAt);
    return totalMs <= 0;
  }).length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">SLA Garantie Leads</h1>
            <p className="text-muted-foreground mt-1">
              Suivi du contact 48h garanti — leads débloqués par les pros
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Urgents (&lt;12h)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <span className="text-2xl font-bold">{urgentCount}</span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Expirés non remboursés</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Timer className="h-5 w-5 text-orange-500" />
                <span className="text-2xl font-bold">{expiredCount}</span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total leads suivis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                <span className="text-2xl font-bold">{data?.total ?? 0}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex items-center gap-4">
          <Select value={filter} onValueChange={(v) => setFilter(v as FilterStatus)}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les leads</SelectItem>
              <SelectItem value="to_contact">À contacter</SelectItem>
              <SelectItem value="contacted_1">Contacté 1x</SelectItem>
              <SelectItem value="expired_unresolved">Expirés non résolus</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card>
          <CardContent className="p-0">
            {isError ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <AlertTriangle className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-lg font-medium">Erreur de chargement</p>
                <p className="text-sm mt-1">Impossible de récupérer les leads SLA</p>
              </div>
            ) : isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-lg font-medium">Aucun lead en attente</p>
                <p className="text-sm mt-1">Tous les leads SLA sont à jour</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Projet</TableHead>
                      <TableHead>Entreprise pro</TableHead>
                      <TableHead>Particulier</TableHead>
                      <TableHead>Débloqué le</TableHead>
                      <TableHead>Éligible remb. le</TableHead>
                      <TableHead>Tentative 1</TableHead>
                      <TableHead>Tentative 2</TableHead>
                      <TableHead className="text-right">Crédits payés</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((item) => {
                      const status = deriveStatus(item);
                      const statusCfg = STATUS_CONFIG[status];
                      const contactCount = (item.contactAttempt1At ? 1 : 0) + (item.contactAttempt2At ? 1 : 0);
                      const isExpired = item.refundEligibleAt
                        ? new Date(item.refundEligibleAt).getTime() <= Date.now()
                        : false;

                      return (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium max-w-[180px] truncate">
                            {item.projectTitle}
                          </TableCell>
                          <TableCell className="max-w-[150px] truncate">{item.proCompanyName}</TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-0.5 text-sm">
                              {item.particulierPhone && (
                                <a href={`tel:${item.particulierPhone}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                                  <Phone className="h-3 w-3" />
                                  {item.particulierPhone}
                                </a>
                              )}
                              {item.particulierEmail && (
                                <a href={`mailto:${item.particulierEmail}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                                  <Mail className="h-3 w-3" />
                                  <span className="max-w-[140px] truncate">{item.particulierEmail}</span>
                                </a>
                              )}
                              {!item.particulierPhone && !item.particulierEmail && (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap">
                            {formatDate(item.unlockedAt)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <div className="flex flex-col gap-0.5">
                              <span className="text-sm">{formatDate(item.refundEligibleAt)}</span>
                              <CountdownCell refundEligibleAt={item.refundEligibleAt} />
                            </div>
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap">
                            {item.contactAttempt1At ? (
                              <span className="text-green-600">{formatDate(item.contactAttempt1At)}</span>
                            ) : contactCount === 0 && !isExpired && canAct ? (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={contactMutation.isPending}
                                onClick={() =>
                                  contactMutation.mutate({ matchId: item.id, attempt: 1 })
                                }
                              >
                                <Phone className="h-3 w-3 mr-1" />
                                Contacter
                              </Button>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap">
                            {item.contactAttempt2At ? (
                              <span className="text-green-600">{formatDate(item.contactAttempt2At)}</span>
                            ) : item.contactAttempt1At && !item.contactAttempt2At && !isExpired && canAct ? (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={contactMutation.isPending}
                                onClick={() =>
                                  contactMutation.mutate({ matchId: item.id, attempt: 2 })
                                }
                              >
                                <Phone className="h-3 w-3 mr-1" />
                                Contacter
                              </Button>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">{item.creditPricePaid ?? 1} cr</TableCell>
                          <TableCell>
                            <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {isExpired && canAct && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() =>
                                  setRefundTarget({
                                    id: item.id,
                                    projectTitle: item.projectTitle,
                                    proCompanyName: item.proCompanyName,
                                  })
                                }
                              >
                                <RefreshCcw className="h-3 w-3 mr-1" />
                                Rembourser
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Précédent
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {data.page} / {data.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= data.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Suivant
            </Button>
          </div>
        )}
      </div>

      <Dialog open={!!refundTarget} onOpenChange={(open) => { if (!open) { setRefundTarget(null); setRefundReason(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer le remboursement</DialogTitle>
            <DialogDescription>
              Vous allez rembourser les crédits pour le projet{" "}
              <strong>{refundTarget?.projectTitle}</strong> débloqué par{" "}
              <strong>{refundTarget?.proCompanyName}</strong>. Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Raison du remboursement (optionnel)</label>
            <Textarea
              placeholder="Ex: Particulier injoignable après 2 tentatives..."
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setRefundTarget(null); setRefundReason(""); }}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              disabled={refundMutation.isPending}
              onClick={() => {
                if (!refundTarget) return;
                refundMutation.mutate({
                  matchId: refundTarget.id,
                  reason: refundReason.trim() || undefined,
                });
              }}
            >
              {refundMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmer le remboursement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
